import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Edit, Download, Menu, Expand } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "../components/ui/pagination";
import { AutoSizeInput } from "./ui/AutoSizeInput";

type CSVEditorProps = {
  file: any;
  content: string;
  onRename: (newName: string) => Promise<any>;
  isRenaming: boolean;
  isStudent: boolean;
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  isDarkMode?: boolean;
};

// RFC-4180-ish CSV parsing for one line: handles quotes, commas, and escaped quotes.
// Note: If multi-line quoted fields are expected, consider Papa Parse instead.
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === '"') {
        const next = line[i + 1];
        if (next === '"') {
          cur += '"';
          i++; // skip escaped quote
        } else {
          inQuotes = false; // closing quote
        }
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
  }
  out.push(cur);
  return out;
}

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const norm = (text ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!norm.trim().length) return { headers: [], rows: [] };

  const lines = norm.split("\n");
  const first = lines[0] ?? "";
  const hasHeader = first.length > 0;

  const headerRow = hasHeader ? parseCsvLine(first) : [];
  const rows = (hasHeader ? lines.slice(1) : lines)
    .filter((ln) => ln.length > 0)
    .map((ln) => parseCsvLine(ln));

  return { headers: headerRow, rows };
}

const CSVEditor: React.FC<CSVEditorProps> = ({
  file,
  content,
  onRename,
  isRenaming: isRenamingProp,
  isStudent,
  isSidebarCollapsed,
  onToggleSidebar,
  isDarkMode = false,
}) => {

  const getNameAndExt = (fullName: string) => {
    const lastDot = fullName.lastIndexOf(".");
    if (lastDot === -1) return { name: fullName, ext: "" };
    return {
      name: fullName.substring(0, lastDot),
      ext: fullName.substring(lastDot),
    };
  };

  const [data, setData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [isRenaming, setIsRenaming] = useState(false);
  const [fileName, setFileName] = useState(file.name);
  const { name: originalName, ext: fileExt } = getNameAndExt(file.name);
  const [nameOnly, setNameOnly] = useState(originalName);

  // Sync filename when file prop changes
  useEffect(() => {
    setFileName(file.name);
    const { name } = getNameAndExt(file.name);
    setNameOnly(name);
  }, [file.name]);

  // Pagination (20 rows/page)
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    if (content && content.length) {
      const { headers: h, rows } = parseCsv(content);
      setHeaders(h);
      setData(rows);
      setPage(1);
    } else {
      setHeaders([]);
      setData([]);
      setPage(1);
    }
  }, [content]);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const start = (page - 1) * pageSize;
  const pagedData = data.slice(start, start + pageSize);

  const handleRename = async () => {
    const trimmedName = nameOnly.trim();
    const newFullName = trimmedName + fileExt;
    if (!trimmedName || newFullName === fileName) {
      setIsRenaming(false);
      setNameOnly(getNameAndExt(fileName).name);
      return;
    }
    const temp = fileName;
    setFileName(newFullName);
    try {
      await onRename(newFullName);
      setNameOnly(getNameAndExt(newFullName).name);
    } catch (error) {
      setFileName(temp);
      setNameOnly(getNameAndExt(fileName).name);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Page links near current page
  const visiblePages = (() => {
    const from = Math.max(1, page - 1);
    const to = Math.min(totalPages, page + 1);
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
  })();

  return (
    <div className="h-full flex flex-col bg-white dark:bg-[#0d1117]">
      {/* Header */}
      <div className="p-4 border-b dark:border-[#30363d] flex-shrink-0 flex items-center justify-between dark:bg-[#161b22]">
        <div className="flex items-center">
          <Button
        variant="ghost"
        onClick={onToggleSidebar}
        className=" inline-flex items-center border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#0d1117] text-xs text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#21262d]"
        title={isSidebarCollapsed ? "Explorer" : "Hide Explorer"}
      >
        {isSidebarCollapsed ? (
          <>
            <Menu className="h-5 w-5" />
          </>
        ) : (
          <>
            <Expand className="h-4 w-4" />
          </>
        )}
      </Button>
          {isRenaming ? (
            <div className="flex items-center">
              <AutoSizeInput
                type="text"
                value={nameOnly}
                onChange={(e) => setNameOnly(e.target.value)}
                onBlur={handleRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleRename();
                }}
                className="font-medium text-gray-800 dark:text-[#f0f6fc] ml-2 focus:outline-none bg-transparent"
                autoFocus
                disabled={isRenamingProp}
              />
              <span className="font-medium text-gray-800 dark:text-[#f0f6fc]">{fileExt}</span>
              {isRenamingProp && (
                <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              )}
            </div>
          ) : (
            <>
              <h3 
                className="font-medium text-gray-800 dark:text-[#f0f6fc] ml-2 cursor-pointer hover:text-blue-600 dark:hover:text-[#f0883e] transition-colors select-none"
                onDoubleClick={() => setIsRenaming(true)}
                title="Double click to rename"
              >
                {fileName}
              </h3>
              {isStudent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsRenaming(true)}
                  className="dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
                >
                  {isRenamingProp ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                  ) : (
                    <Edit className="text-gray-800 dark:text-[#c9d1d9]" size={14} />
                  )}
                </Button>
              )}
            </>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDownload}
            className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
          >
            <Download className="h-4 w-4 mr-2" /> Download
          </Button>
        </div>
      </div>

      {/* Body + Footer separated to prevent extra scroll */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Scrollable table area */}
        <div className="flex-1 min-h-0 overflow-auto px-4 pt-4 pb-4">
          <div className="border rounded-lg overflow-hidden dark:border-[#30363d]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-[#161b22]">
                <tr>
                  {headers.map((header, index) => (
                    <th
                      key={index}
                      className="px-4 py-2 text-left font-medium text-gray-900 dark:text-[#f0f6fc] border-r dark:border-[#30363d] last:border-r-0"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedData.map((row, rowIndex) => {
                  const absoluteRowIndex = start + rowIndex;
                  return (
                    <tr
                      key={absoluteRowIndex}
                      className="border-t dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#21262d]"
                    >
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="px-4 py-2 border-r dark:border-[#30363d] last:border-r-0 text-gray-700 dark:text-gray-300"
                        >
                          <span>{cell}</span>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Fixed footer (not inside scroll) */}
        <div className="flex items-center justify-between px-4 pb-4 pt-3 border-t dark:border-[#30363d] bg-white dark:bg-[#161b22]">
          <span className="text-xs text-gray-600 dark:text-[#8b949e]">
            Page {page} of {totalPages} • Showing {pagedData.length} of{" "}
            {data.length} rows
          </span>

          <Pagination className="border-none">
            <PaginationContent className="dark:text-[#c9d1d9]">
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className="border-none dark:hover:bg-[#21262d] dark:text-[#c9d1d9]"
                  aria-disabled={page === 1}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) setPage(page - 1);
                  }}
                />
              </PaginationItem>

              {page > 2 && (
                <>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      isActive={page === 1}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(1);
                      }}
                      className={cn(page === 1 && "dark:bg-[#f0883e] dark:text-white")}
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>
                  {page > 3 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                </>
              )}

              {visiblePages.map((p) => (
                <PaginationItem key={p}>
                  <PaginationLink
                    href="#"
                    isActive={p === page}
                    onClick={(e) => {
                      e.preventDefault();
                      setPage(p);
                    }}
                    className={cn(p === page && "dark:bg-[#f0883e] dark:text-white", p !== page && "dark:hover:bg-[#21262d]")}
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ))}

              {page < totalPages - 1 && (
                <>
                  {page < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      isActive={page === totalPages}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(totalPages);
                      }}
                      className={cn(page === totalPages && "dark:bg-[#f0883e] dark:text-white")}
                    >
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                </>
              )}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  className="border-none dark:hover:bg-[#21262d] dark:text-[#c9d1d9]"
                  aria-disabled={page === totalPages}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page < totalPages) setPage(page + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </div>
  );
};

export default CSVEditor;
